import React, { useState, useEffect } from 'react'
import { Cpu, AlertCircle, CheckCircle } from 'lucide-react'
import { getOpenAIApiKey, isDemoModeFlag, isUserSuperAdmin } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const OpenAIStatus: React.FC = () => {
  const { user } = useAuth()
  const [status, setStatus] = useState<'checking' | 'active' | 'inactive'>('checking')
  const [error, setError] = useState<string | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    checkAdminStatus()
  }, [user])

  useEffect(() => {
    if (isSuperAdmin) {
      checkOpenAIStatus()
    }
  }, [user, isSuperAdmin])

  const checkAdminStatus = async () => {
    if (!user) {
      setIsSuperAdmin(false)
      setIsVisible(false)
      return
    }

    try {
      const superAdmin = await isUserSuperAdmin(user.email)
      setIsSuperAdmin(superAdmin)
      setIsVisible(superAdmin)
      
      console.log(`User ${user.email} super admin status:`, superAdmin)
    } catch (error) {
      console.error('Error checking admin status:', error)
      setIsSuperAdmin(false)
      setIsVisible(false)
    }
  }

  const checkOpenAIStatus = async () => {
    if (!isSuperAdmin) {
      setIsVisible(false)
      return
    }

    try {
      // In demo mode, always show mock generation
      if (isDemoModeFlag) {
        setStatus('inactive')
        setError('Demo mode - configure Supabase to enable AI generation')
        return
      }

      // Check if API key is available
      const apiKey = await getOpenAIApiKey()
      if (apiKey) {
        setStatus('active')
        setError(null)
        console.log('AI API key successfully retrieved for super admin')
      } else {
        setStatus('inactive')
        setError('AI API key not configured - using mock generation')
      }
    } catch (err) {
      setStatus('inactive')
      setError(err instanceof Error ? err.message : 'API check failed - using mock generation')
    }
  }

  // Don't render anything if user is not a super admin
  if (!isVisible || !isSuperAdmin) {
    return null
  }

  const getStatusColor = () => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200'
      case 'inactive': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />
      case 'inactive': return <AlertCircle className="w-4 h-4" />
      default: return <Cpu className="w-4 h-4 animate-spin" />
    }
  }

  const getStatusText = () => {
    switch (status) {
      case 'active': return 'AI Generation Active'
      case 'inactive': return 'Mock Generation Mode'
      default: return 'Checking AI Status...'
    }
  }

  const getStatusDescription = () => {
    if (isDemoModeFlag) return 'Demo mode - configure Supabase'
    
    switch (status) {
      case 'active': return 'AI image generation enabled'
      case 'inactive': return 'Using fallback image generation'
      default: return 'Verifying API connection...'
    }
  }

  return (
    <div className="mb-2">
      <div className="text-xs text-gray-500 mb-1">Super Admin Panel</div>
      <div className={`inline-flex items-center space-x-2 px-3 py-2 rounded-lg border ${getStatusColor()}`}>
        {getStatusIcon()}
        <div className="flex flex-col">
          <span className="text-sm font-medium">{getStatusText()}</span>
          <span className="text-xs opacity-75">{getStatusDescription()}</span>
        </div>
        {error && status === 'inactive' && !isDemoModeFlag && (
          <button
            onClick={checkOpenAIStatus}
            className="text-xs underline opacity-75 hover:opacity-100"
            title={`Error: ${error}`}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  )
}

export default OpenAIStatus 